import { getDashboardProgram, getProgramEnrollmentsSummary } from '../actions/programActions'
import { createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { addProgramEnrollmentsManually, deleteEnrollment, updateEnrollment } from '../actions/enrollmentActions'
import { getProgramSchedule } from '../actions/scheduleActions'
import { deleteScheduleSession, updateScheduleSession } from '../actions/scheduleSessionActions'
import { defaultScheduleSessionCompareFunction, programRosterUserEnrollmentCompareFunction } from '../utility/helperFunctions'
import {
  addStudentsToWaitlistManually,
  addStudentToProgram,
  inviteStudentToProgram,
  moveEntireWaitlist,
  removeStudentFromWaitlist,
  transferStudentToProgram,
} from '../actions/waitlistActions'

const selectedStudentsEntityAdapter = createEntityAdapter()
const selectedProgramsEntityAdapter = createEntityAdapter()
const enrollmentsEntityAdapter = createEntityAdapter()
const waitlistEntityAdapter = createEntityAdapter()
const selectedSeriesEntityAdapter = createEntityAdapter()
const selectedCoachesEntityAdapter = createEntityAdapter()

const initialState = {
  program: null,
  enrollmentsSummary: null,
  enrollments: enrollmentsEntityAdapter.getInitialState(),
  waitlist: waitlistEntityAdapter.getInitialState(),
  waitlistActionSuccessMessage: null,
  errors: null,
  addStudentsModal: {
    selectedStudents: selectedStudentsEntityAdapter.getInitialState(),
    selectedPrograms: selectedProgramsEntityAdapter.getInitialState(),
  },
  postAnnouncementModal: {
    selectedPrograms: selectedProgramsEntityAdapter.getInitialState(),
    selectedSeries: selectedSeriesEntityAdapter.getInitialState(),
    selectedCoaches: selectedCoachesEntityAdapter.getInitialState(),

  },
}

const programDashboardReducer = createSlice({
  name: 'programDashboard',
  initialState,
  reducers: {
    resetDashboard: () => initialState,
    resetSelectedData: state => {
      selectedStudentsEntityAdapter.setAll(state.addStudentsModal.selectedStudents, [])
      selectedProgramsEntityAdapter.setAll(state.addStudentsModal.selectedPrograms, [])
      selectedSeriesEntityAdapter.setAll(state.postAnnouncementModal.selectedSeries, [])
      selectedCoachesEntityAdapter.setAll(state.postAnnouncementModal.selectedCoaches, [])
    },
    resetSelectedStudents: (state) => {
      selectedStudentsEntityAdapter.setAll(state.addStudentsModal.selectedStudents, [])
    },
    resetSelectedCoaches: (state) => {
      selectedCoachesEntityAdapter.setAll(state.postAnnouncementModal.selectedCoaches, [])
    },
    addSelectedStudent: (state, action) => {
      selectedStudentsEntityAdapter.addOne(state.addStudentsModal.selectedStudents, action.payload)
    },
    updateSelectedStudent: (state, action) => {
      selectedStudentsEntityAdapter.setOne(state.addStudentsModal.selectedStudents, action.payload)
    },
    deleteSelectedStudent: (state, action) => {
      selectedStudentsEntityAdapter.removeOne(state.addStudentsModal.selectedStudents, action.payload)
    },
    addSelectedProgram: (state, action) => {
      selectedProgramsEntityAdapter.addOne(state.addStudentsModal.selectedPrograms, action.payload)
    },
    deleteSelectedProgram: (state, action) => {
      selectedProgramsEntityAdapter.removeOne(state.addStudentsModal.selectedPrograms, action.payload)
    },
    addSelectedSeries: (state, action) => {
      selectedSeriesEntityAdapter.addOne(state.postAnnouncementModal.selectedSeries, action.payload)
    },
    deleteSelectedSeries: (state, action) => {
      selectedSeriesEntityAdapter.removeOne(state.postAnnouncementModal.selectedSeries, action.payload)
    },
    addSelectedCoach: (state, action) => {
      selectedCoachesEntityAdapter.addOne(state.postAnnouncementModal.selectedCoaches, action.payload)
    },
    deleteSelectedCoach: (state, action) => {
      selectedCoachesEntityAdapter.removeOne(state.postAnnouncementModal.selectedCoaches, action.payload)
    },
    addSelectedStudents: (state, action) => {
      selectedStudentsEntityAdapter.addMany(state.addStudentsModal.selectedStudents, action.payload)
    },
    addSelectedCoaches: (state, action) => {
      selectedCoachesEntityAdapter.addMany(state.postAnnouncementModal.selectedCoaches, action.payload)
    },
    deleteSelectedStudents: (state, action) => {
      selectedStudentsEntityAdapter.removeMany(state.addStudentsModal.selectedStudents, action.payload)
    },
    deleteSelectedCoaches: (state, action) => {
      selectedCoachesEntityAdapter.removeMany(state.postAnnouncementModal.selectedCoaches, action.payload)
    },
    clearWaitlistActionSuccessMessage: (state) => {
      state.waitlistActionSuccessMessage = null
    },
  },
  extraReducers: builder => {
    builder
      // Get Program Overview Data
      .addCase(getDashboardProgram.fulfilled, (state, action) => {
        state.program = action.payload.course
        enrollmentsEntityAdapter.setAll(state.enrollments, action.payload.course.enrollments)

        if (action.payload.course.hasWaitlistEnabled) {
          waitlistEntityAdapter.setAll(state.waitlist, action.payload.course.waitlist)
        }
      })
      .addCase(getDashboardProgram.rejected, (state, action) => {
        state.errors = action.payload
      })

      // Get Program Enrollments Summary
      .addCase(getProgramEnrollmentsSummary.fulfilled, (state, action) => {
        state.enrollmentsSummary = action.payload.enrollmentsSummary
      })

      // Add student manually
      .addCase(addProgramEnrollmentsManually.fulfilled, (state, action) => {
        const enrollments = action.payload.enrollments.created

        state.program.golferLimit = enrollments[0].course.golferLimit
        enrollmentsEntityAdapter.addMany(state.enrollments, enrollments)

        if (state.program.hasWaitlistEnabled) {
          waitlistEntityAdapter.removeMany(state.waitlist, action.payload.removedWaitlistEntries)
        }
      })

      // Update enrollment
      .addCase(updateEnrollment.fulfilled, (state, action) => {
        const enrollment = action.payload.enrollment

        if (state.program) {
          enrollmentsEntityAdapter.setOne(state.enrollments, enrollment)
        }
      })

      // Delete enrollment
      .addCase(deleteEnrollment.fulfilled, (state, action) => {
        const enrollmentId = action.payload.enrollment.id

        if (state.program) {
          enrollmentsEntityAdapter.removeOne(state.enrollments, enrollmentId)
        }
      })

      // Get Schedule Tab data
      .addCase(getProgramSchedule.fulfilled, (state, action) => {
        if (action.payload.reducerName === 'programDashboard') {
          state.schedule = action.payload.schedule
        }
      })

      // Update single Schedule Session
      .addCase(updateScheduleSession.fulfilled, (state, action) => {
        if (state.schedule) {
          const index = state.schedule.scheduleSessions.findIndex(session => session.id === action.payload.scheduleSession.id)
          state.schedule.scheduleSessions[index] = action.payload.scheduleSession
          state.schedule.scheduleSessions.sort(defaultScheduleSessionCompareFunction)
        }
      })

      // Delete Single Schedule Session
      .addCase(deleteScheduleSession.fulfilled, (state, action) => {
        if (state.schedule) {
          const index = state.schedule.scheduleSessions.findIndex(session => session.id === action.payload.scheduleSession.id)
          state.schedule.scheduleSessions.splice(index, 1)
        }
      })

      .addCase(removeStudentFromWaitlist.fulfilled, (state, action) => {
        waitlistEntityAdapter.removeOne(state.waitlist, action.payload.id)
        state.waitlistActionSuccessMessage = {
          title: 'Removed from waitlist',
        }
      })

      .addCase(addStudentToProgram.fulfilled, (state, action) => {
        const enrollment = action.payload.enrollment

        state.program.golferLimit = enrollment.course.golferLimit
        waitlistEntityAdapter.removeOne(state.waitlist, action.payload.waitlistEntryId)
        state.waitlistActionSuccessMessage = {
          title: `Enrollment(s) Successfully Moved from the waitlist and added to ${action.payload.enrollment?.course?.name} program`,
          message: 'An email was sent notifying the students.',
        }
        enrollmentsEntityAdapter.addOne(state.enrollments, enrollment)
      })

      .addCase(transferStudentToProgram.fulfilled, (state, action) => {
        waitlistEntityAdapter.removeOne(state.waitlist, action.payload.waitlistEntryId)
        state.waitlistActionSuccessMessage = {
          title: `Enrollment(s) Successfully Moved from the waitlist and added to ${action.payload.enrollment?.course?.name} program`,
          message: 'An email was sent notifying the students.',
        }
      })

      .addCase(moveEntireWaitlist.fulfilled, (state, action) => {
        waitlistEntityAdapter.removeMany(state.waitlist, action.payload.movedWaitlistEntryIds ?? [])

        // When the target is the current program, reflect the new enrollments in its roster/count.
        const currentProgramEnrollments = (action.payload.enrollments ?? []).filter(e => e.course?.id === state.program?.id)
        if (currentProgramEnrollments.length) {
          enrollmentsEntityAdapter.addMany(state.enrollments, currentProgramEnrollments)
          state.program.golferLimit = currentProgramEnrollments[0].course.golferLimit
        }

        const movedToName = (action.payload.enrollments ?? [])[0]?.course?.name

        state.waitlistActionSuccessMessage = {
          title: movedToName
            ? `Enrollment(s) Successfully Moved from the waitlist and added to ${movedToName} program`
            : 'Enrollment(s) Successfully Moved from the waitlist',
          message: 'An email was sent notifying the students.',
        }
      })

      .addCase(inviteStudentToProgram.fulfilled, (state, action) => {
        waitlistEntityAdapter.setOne(state.waitlist, action.payload.waitlistEntry)
        state.waitlistActionSuccessMessage = {
          title: 'Registration Link Sent',
          message: 'Shortly, they will receive an email',
        }
      })

      .addCase(addStudentsToWaitlistManually.fulfilled, (state, action) => {
        waitlistEntityAdapter.setAll(state.waitlist, action.payload.waitlist)
      })
  },
})

export const {
  resetDashboard,
  resetSelectedData,
  addSelectedProgram,
  addSelectedStudent,
  updateSelectedStudent,
  deleteSelectedProgram,
  deleteSelectedStudent,
  addSelectedSeries,
  deleteSelectedSeries,
  addSelectedCoach,
  deleteSelectedCoach,
  addSelectedCoaches,
  addSelectedStudents,
  deleteSelectedCoaches,
  deleteSelectedStudents,
  resetSelectedStudents,
  resetSelectedCoaches,
  clearWaitlistActionSuccessMessage,
} = programDashboardReducer.actions

export const {
  selectAll: selectSelectedStudents,
} = selectedStudentsEntityAdapter.getSelectors(state => state.programDashboard.addStudentsModal.selectedStudents)

export const {
  selectAll: selectSelectedCoaches,
} = selectedCoachesEntityAdapter.getSelectors(state => state.programDashboard.postAnnouncementModal.selectedCoaches)

export const {
  selectAll: selectSelectedPrograms,
} = selectedProgramsEntityAdapter.getSelectors(state => state.programDashboard.addStudentsModal.selectedPrograms)

export const {
  selectAll: selectAllEnrollments,
} = enrollmentsEntityAdapter.getSelectors(state => state.programDashboard.enrollments)

export const selectAllEnrollmentsRosterData = createSelector(
  selectAllEnrollments,
  enrollments => enrollments.toSorted(programRosterUserEnrollmentCompareFunction).map(e => ({ enrollment: e }))
)

export const selectEnrolledStudents = createSelector(
  selectAllEnrollments,
  enrollments => enrollments?.map(e => e.user)
)

// Students with a dropped enrollment. The dashboard's `enrollments` payload is not_dropped, so on a
// fresh load these only come from the course's `droppedStudentIds`; after an in-session E&B drop the
// dropped enrollment also lingers in the store (covered by selectEnrolledStudents above). Both paths
// keep dropped students marked as already-in-program in the Add Students list.
export const selectDroppedStudentIds = createSelector(
  state => state.programDashboard.program,
  program => program?.droppedStudentIds ?? []
)

export const {
  selectAll: selectSelectedSeries,
} = selectedSeriesEntityAdapter.getSelectors(state => state.programDashboard.postAnnouncementModal.selectedSeries)

export const {
  selectAll: selectWaitlist,
} = waitlistEntityAdapter.getSelectors(state => state.programDashboard.waitlist)

export const selectWaitlistedStudents = createSelector(
  selectWaitlist,
  waitlist => waitlist?.map(w => w.user)
)

export default programDashboardReducer.reducer
